/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * created by cmiller
 */

/** Base class for the top or right visible margins of the plot. */

export class MarginView {
    constructor() {
        this.titleFullText = ""; // top: plot title, right: attribute name
        this.isDefaultTitle = false;
    }

    destroy() {
        // for future use; nothing to do at the moment
    }

    /**
     * Set the plot title (if top margin), or legend prompt string (if right margin).
     * @param titleString - new title string (empty string is OK)
     * @param forceUpdate - (OBSOLETE)
     * @param isDefaultTitle - true if this is the default title provided by setDefaultPlotTitle()
     */
    setTitle( titleString, forceUpdate, isDefaultTitle ) {
        // #1390 restrictions on setting title moved to Dyn.PlotShaper.setDefaultPlotTitle()
        this.titleFullText = titleString;
        this.isDefaultTitle = Boolean( isDefaultTitle ); // for save()
        this.updateVisuals();
        // console.log( "setTitle()", this.isHorizontal ? "plot title:":"legend prompt:", "'"+this.titleFullText+"'", "is default", this.isDefaultTitle );
    }

    /**
     * @returns {String} - returns title string
     */
    getTitle() {
        "use strict";
        return this.titleFullText; //this.title.attr("text");
    }

    /**
     * @returns {MarginView.legendThickness|*} -- desired view thickness (right margin:width, top margin:height) in pixels.
     */
    calcThickness() {
        // subclasses override
    }

    needsNewThickness() {
        "use strict";
        var currentThickness = this.bounds.widthOrHeight( this.isHorizontal ),
            newThickness = this.calcThickness();

        return( newThickness !== currentThickness );
    }
}
export const wrapDynTopMarginView = function(Dyn) {
	let mediator = Dyn.mediator;

    /***************************************
     *
     */
    class TopMarginView extends MarginView {

        constructor( w, h, plotPaper ) {
            super();
            this.bounds = new Dyn.mathUtil.Rect( 0, 0, w, h );
            this.titleRect = new Dyn.mathUtil.Rect( 0, 0, w, h );
            this.isHorizontal = true;
            this.axisID = 'top';

            // Raphael elements
            this.paper = plotPaper;
            this.title = null;
            this.subTitle = null; // NOTE: not currently in use; saved for possible future use
            this.titleEditButton = null;
            this.closebox = null;
            this.menuButton = null; // context menu button used by some plot types
            this.softResetButton = null; // optional button to reset to the initial plot state
            this.backgroundRect = null; // partially opaque when title is over a map plot, for readability

            this.excludedCasesWidth = 0; // excluded cases display which we don't want to overlap with title
        };

        /**
         * Save the top view to a data object that can be JSONified
         * @returns {{}} - data to be used by restore()
         */
        save() {
            var marginModel = {};
            // save the chart title; escape the single quote
            // don't save default title, so we can restore to a different default if language has changed #1389
            if( ! this.isDefaultTitle ) {
                marginModel.chartTitleText = this.getTitle().replace( /'/g, "\\'" );
            }
            return marginModel;
        }

        /**
         * Change top and right margin view to match the saved data.
         */
        restore( savedData ) {
            "use strict";
            if( this.hasSavedPlotTitle( savedData )) {
                // Unescape the single quote
                this.setTitle( savedData.chartTitleText.replace(/\\'/g,"'") );
            }
        }

        hasSavedPlotTitle( savedData ) {
            return (typeof savedData.chartTitleText ==='string'); // restore blank or non-blank title string
        }

        /** initialize visual elements (SVG/Raphael elements). See also .updateVisuals() and .layerVisuals() */
        initVisuals( titleString ) {
            "use strict";
            var marginView    = this,
                titleFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotTitleFontSize ),
                subTitleHeight = titleFontSize / this.kTopAndBottomMarginFraction,
                viewOnly = Dyn.preferences.isViewOnly();

            this.backgroundRect = this.paper.rect( 0,0,0,0 )
                .attr({
                    fill: Dyn.plotUtil.normalDropColor,
                    stroke: "none",
                    opacity: Dyn.plotUtil.kLegendOpacity // show map behind legend
                });

            this.titleFullText = titleString;
            this.title         = this.paper.text(
                this.bounds.centerX(),
                this.bounds.centerY(), titleString ).attr( {
                "font-size": titleFontSize,
                "font-family": Dyn.plotUtil.plotTitleFontFamily,
                "cursor": 'pointer',
                "class": 'tde-plot-title no-print',
            } );
            if( ! viewOnly ) {
                this.title.attr({
                    "cursor": 'pointer'
                });
            }
            // make title node focusable
            this.title.node.setAttribute( "focusable", "true" );
            this.title.node.setAttribute( "tabindex", "0" );
            this.title.node.setAttribute( "id", Dyn.plotUtil.plotTitleId);
            // make title semantically a heading for accessibility
            this.title.node.setAttribute( "role", "heading" );
            this.title.node.setAttribute( "aria-level", "2" );

            /* this.title.hover(
                function ( e ) {
                    e.currentTarget.style.cursor = Dyn.plotUtil.cursorPointer;
                }, function ( e ) {
                    e.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
                } ); */

            this.subTitle = this.paper.text(
                this.bounds.centerX(),
                this.bounds.y + subTitleHeight, "" ).attr( {
                "font-size": Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotSubtitleFontSize ),
                "class": 'tde-plot-title',
                } );
            this.subTitle.node.setAttribute("id", Dyn.plotUtil.plotSubTitleId);

            // enable inline Text Editing
            this.paper.inlineTextEditing( this.title, {type: 'text', ariaLabel: 'Graph Title'} );

            // Create edit button
            const buttonOptions = Dyn.plotUtil.roundRectAxisButtonOptions();
            this.titleEditButton = Dyn.plotUtil.makeSquareButton(
                this.paper,
                Dyn.plotUtil.getPencilIcon( Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.plotTitlePencilSize )),
                hoverIn, hoverOut, titleClick,
                false, Dyn.loc( "edit plot title"), Dyn.plotUtil.editPlotTitleButtonId, buttonOptions );
            // Set required attributes for edit button
            this.titleEditButton
                .data( 'type', "editButton" )
                .data( "tooltip", Dyn.loc( "edit plot title"))
                .attr( 'cursor', Dyn.plotUtil.cursorPointer );

            this.closebox = Dyn.plotUtil.makeCloseBox(
                this.paper, hoverIn, hoverOut, clickedOnClosebox ,
                Dyn.loc(Dyn.plotUtil.plotTitleCloseButtonAriaLabel), Dyn.plotUtil.plotTitleCloseButtonId,
                buttonOptions );
            this.closebox.data( "tooltip", Dyn.loc( "remove plot title"));

            this.menuButton = Dyn.plotUtil.makeContextMenuButton(
                this.paper, clickedOnMenu,
                {}, /* keyhandler default is Enter==clickHandler */
                undefined /* aria label undefined */,
                0, // updated to the current plot by contextMenuOptions()
                buttonOptions );

            let softResetTipAndLabel = Dyn.loc( "Reload Plot");
            this.softResetButton = Dyn.plotUtil.makeSquareButton(
                this.paper,
                Dyn.plotUtil.getSoftResetIcon( Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.plotTitleSoftResetIconSize)),
                hoverIn, hoverOut, clickedOnSoftReset,
                {}, // keyhandler default is Enter==clickHandler
                softResetTipAndLabel, // aria label
                Dyn.plotUtil.softResetTitleButtonId,
                buttonOptions )
                .data( 'type', "softResetButton" )
                .data( "tooltip", softResetTipAndLabel )
                .attr( 'cursor', Dyn.plotUtil.cursorPointer );

            function hoverIn( e ) {
                let symbol = this,
                    cover = this.getCoverElement(),
                    isCloseBox = (cover === marginView.closebox.getCoverElement());
                cover.attr({ fill: Dyn.plotUtil.axisButtonBgDownColor });
                symbol.attr( { fill: Dyn.plotUtil.axisButtonDownColor,
                    stroke: isCloseBox ? Dyn.plotUtil.axisButtonDownColor : "none" } );
                if( this.data("tooltip"))
                    Dyn.toolTip.show( this, e, { x:cover.getBBox().x2, y:cover.getBBox().y2, alignLeft: true });
            }
            function hoverOut( e ) {
                let symbol = this,
                    cover = this.getCoverElement();
                cover.attr({ fill: Dyn.plotUtil.axisButtonBgUpColor });
                symbol.attr( { fill: Dyn.plotUtil.axisButtonUpColor, stroke: "none" } );
                if( Dyn.toolTip.isMyElement( this ))
                    Dyn.toolTip.hide();
            }
            function clickedOnClosebox( /*e */ ) {
                // handler to remove title from plot
                marginView.setTitle("", true );
                marginView.updateVisuals();
            }

            function clickedOnMenu( event ) {
                Dyn.plotView.plotShaper.handleContextMenuClick( event, this /* symbol element */ );
            }

            function clickedOnSoftReset( event ) {
                if( Dyn.undoManager.hasUndoSteps() // if something to undo
                    || Dyn.plotUtil.isTuvaJr()) { // or if we might be coming from Play view, which changes the plot state without undo steps
                    Dyn.plotView.reloadOriginalPlotState();
                }   // else ignore button, as we are already at original state
            }

            // Start inline editing on click
            function titleClick(e) {
                e.stopPropagation();
                e.preventDefault()
                var input,
                    thisTextElement = marginView.title;

                thisTextElement.attr( "text", marginView.titleFullText ); // restore full text if ellided
                thisTextElement.data("inTextEdit",true);
                input = thisTextElement.inlineTextEditing.startEditing( {
                    color: Dyn.plotUtil.titleColor,
                    bgColor: Dyn.plotUtil.normalDropColor });
                // Hide buttons in editing mode (with focus outline cleanup for Safari)
                Dyn.plotUtil.showOrHideElement(marginView.titleEditButton, false);
                Dyn.plotUtil.showOrHideElement(marginView.closebox, false);
                Dyn.plotUtil.showOrHideElement(marginView.menuButton, false);
                Dyn.plotUtil.showOrHideElement(marginView.softResetButton, false);
                mediator.publish( "plot:startViewMode", {  // allow plot view click to blur
                    mode: "text-editing",
                    textElement: thisTextElement,
                    stopEditing: stopEditing });

                input.addEventListener( "blur", function( e ) {
                    // Stop inline editing after blur on the text field
                    stopEditing( e );
                }, false );

                input.addEventListener( "keypress", function ( e ) {
                    // Stop inline editing after entering Return/Enter key (key code 13)
                    var key = e.which || e.keyCode;
                    if ( key === 13 ) {
                        stopEditing( e );
                    }
                }, false );

                function stopEditing( event ) {
                    thisTextElement.node.setAttribute("class", "tde-plot-title");
                    if( thisTextElement.data("inTextEdit") ) {
                        thisTextElement.data("inTextEdit",false);
                        if( event )
                            event.stopImmediatePropagation();
                        thisTextElement.inlineTextEditing.stopEditing();
                        let fullText = thisTextElement.attr( "text" );
                        if( /^[\s_]*$/.test(fullText)) {
                            fullText = ""; // replace "  ", or "_" with blank title
                            thisTextElement.attr( "text", fullText );
                        }
                        marginView.setTitle( fullText, true /* forceUpdate */ ); // save full text and ellide
                        mediator.publish( "plot:stopViewMode", {mode:"text-editing"} ); // notify plot view
                        Dyn.plotUtil.showOrHideElement(marginView.titleEditButton, true); // Show the edit button after editing
                        // Focus back source element after editing is done when the editing triggered through keyboard
                        if(e.type === 'keydown') {
                            e.target && e.target.focus();
                        }
                    }
                }
            }

            // To set click event for edit button and title
            if( ! viewOnly ){
                this.title.click(titleClick);
                // Listen for keydown event for title
                this.title.node.onkeydown = function(e) {
                    if( e.key === "Enter" ){
                        titleClick(e);
                    }
                }
            }
        }

        /**
         * re-layer the Rafael visual elements so the correct ones are in front (z-axis).
         * designed to be compatible with Dyn.PlotView.layerVisuals
         */
        layerVisuals() {
            // Note: this sequence also affects keyboard focus order of the buttons
            if( this.backgroundRect ) {
                this.backgroundRect.toFront();
            }
            if( this.title ) {
                this.title.toFront();
            }
            if( this.subTitle ) {
                this.subTitle.toFront();
            }
            if( this.titleEditButton ) {
                this.titleEditButton.toFront();
            }
            if( this.menuButton ) {
                this.menuButton.toFront();
            }
            if( this.closebox ) {
                this.closebox.toFront();
            }
            if( this.softResetButton ) {
                this.softResetButton.toFront();
            }
        };

        setFocus() {
            if( Dyn.plotUtil.isElementVisible( this.title ) && this.titleFullText.length > 0 ) {
                this.title.node.focus(); // when title text is visible with non-empty string
            } else if( Dyn.plotUtil.isElementVisible( this.titleEditButton.getCoverElement())) {
                this.titleEditButton.getCoverElement().node.focus(); // when titleFullText===""
            }
            // else if title hidden, no elements are visible; there is nothing here to focus
        }

        /**
         * Update the top margin visual elements, e.g. on new bounding box, or new attribute.
         */
        updateVisuals( optionalViewMode ) {
            const viewMode = optionalViewMode  || new Dyn.PlotViewMode(),
                showControls = viewMode.showControls(),
                showPlotTitle = Dyn.preferences.isPlotTitleVisible(),
                showEditButton = showControls && showPlotTitle,
                showContextMenu = showControls && Dyn.plotView.plotShaper.wantPlotContextMenu(),
                showCloseBox = showControls && showPlotTitle && (this.titleFullText.length > 0),
                showSoftResetButton = showControls && Dyn.plotView.hasOriginalPlotState() && ! Dyn.plotUtil.isDrawOnly(),
                numVisibleButtons = [showEditButton,showContextMenu, showCloseBox, showSoftResetButton]
                    .reduce((a, b) => a+=( b ? 1 : 0), 0);

            // update background rect for maps
            const legendBounds = Dyn.plotView.rightMargin.bounds;
            this.backgroundRect.attr( {
                x: this.bounds.x,
                y: this.bounds.y,
                width: Math.max(0, legendBounds.right() - this.bounds.left()),
                height: this.bounds.height,
        		fill: Dyn.plotUtil.normalDropColor,
        		opacity: Dyn.plotUtil.kLegendOpacity });
            Dyn.plotUtil.showOrHideElement( this.backgroundRect, showPlotTitle );

            // 1st pass: title is centered: show/hide/position
            let buttonSpacing = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize ) + Dyn.plotUtil.plotButtonSpacing,
                titleFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotTitleFontSize ),
                subTitleHeight = titleFontSize / this.kTopAndBottomMarginFraction,
                titleRect = this.titleRect.copyRect( this.bounds );
            this.title.attr({ x: titleRect.centerX(), y: titleRect.centerY(),
                text: this.titleFullText, 'font-size':titleFontSize, fill: Dyn.plotUtil.titleColor });
            Dyn.plotUtil.showOrHideElement( this.title, showPlotTitle );

            // 2nd pass: clip title and move text to available space in title bounds?
            const firstButtonOffset = 2; // because otherwise it is too close to title
            let bbox = Dyn.plotUtil.clipStringToWidth( this.title, titleRect.width ),
                freeSpaceToRight = (titleRect.width - bbox.width)/2,
                buttonAndExcludedCasesWidth = (buttonSpacing * numVisibleButtons) + this.excludedCasesWidth,
                buttonCenterX, buttonCenterY = titleRect.centerY();
            if( (this.titleFullText.length===0) || !showPlotTitle ) {
                // no title, but we may have buttons to show
                titleRect.width = buttonSpacing * numVisibleButtons;
                titleRect.x = this.bounds.centerX() - titleRect.width/2;
                buttonCenterX = titleRect.x + buttonSpacing/2;
            } else {
                if( freeSpaceToRight < buttonAndExcludedCasesWidth ) {
                    // full title overlaps with buttons and excluded-cases display; clip+position title again to fit
                    titleRect.width = Math.max( 0, this.bounds.width - buttonAndExcludedCasesWidth );
                    this.title.attr({ x: titleRect.centerX(), text: this.titleFullText });
                    bbox = Dyn.plotUtil.clipStringToWidth( this.title, titleRect.width );
                    titleRect.x = bbox.x;
                    titleRect.width = bbox.width + (buttonSpacing * numVisibleButtons) + firstButtonOffset;
                    buttonCenterX = bbox.x2 + buttonSpacing/2 + firstButtonOffset;
                } else {
                    // full title fits; keep it centered over plot
                    titleRect.x = bbox.x;
                    titleRect.width = bbox.width;
                    buttonCenterX = bbox.x2 + buttonSpacing/2 + firstButtonOffset;
                }
            }
            this.titleRect.addDebugRect( this.paper, "green" );

            // edit button: show/hide/position to right of title
            this.titleEditButton.transform( "t" + buttonCenterX + "," + buttonCenterY );
            Dyn.plotUtil.showOrHideElement( this.titleEditButton, showEditButton );
            if( showEditButton ) buttonCenterX += buttonSpacing;

            // context menu button: show/hide/position to right of previous button
            this.menuButton.transform( "t" + buttonCenterX + "," + buttonCenterY );
            Dyn.plotUtil.showOrHideElement( this.menuButton, showContextMenu );
            if( showContextMenu ) buttonCenterX += buttonSpacing;

            // closebox: show/hide/position to right of previous button
            this.closebox.transform( "t" + buttonCenterX + "," + buttonCenterY );
            Dyn.plotUtil.showOrHideElement( this.closebox, showCloseBox );
            if( showCloseBox ) buttonCenterX += buttonSpacing;

            // soft reset button: show/hide/position to right of previous button
            this.softResetButton.transform( "t" + buttonCenterX + "," + buttonCenterY );
            Dyn.plotUtil.showOrHideElement( this.softResetButton, showSoftResetButton );
            if( showSoftResetButton ) buttonCenterX += buttonSpacing;

            // update tooltips for current language
            this.titleEditButton.data( 'tooltip', Dyn.loc( "edit plot title"));
            this.closebox.data( 'tooltip', Dyn.loc( "remove plot title"));

            // subtitle (currently unused)
            this.subTitle.attr({ x: titleRect.centerX(), y:this.bounds.y + subTitleHeight, "font-size": Dyn.preferences.getFontSize(  Dyn.plotUtil.kPlotSubtitleFontSize ) });
        }

        /**
         * set or clear image-ready or view-only mode
         * @param mode ({Dyn.PlotViewMode}|{Dyn.PlotModeImageReady}) - new mode settings
         */
        setViewMode( mode ) {
            this.updateVisuals( mode );
        }

        /**
         * @param {number} w - width of excluded cases display
         */
        setExcludedCasesWidth( w ) {
            this.excludedCasesWidth = w;
            this.updateVisuals(); // we may need to reposition title
        }

        /** Show or hide the plot context menu in title? */
        showHidePlotMenu( wantPlotContextMenu ) {
            let wantMenu = wantPlotContextMenu && ! Dyn.preferences.isViewOnly(),
                menuVisible = this.menuButton && Dyn.plotUtil.isElementVisible( this.menuButton );
            if( wantMenu ) {
                // update the context menu for the current plot type
                let options = Dyn.plotView.plotShaper.contextMenuOptions(),
                    cover = this.menuButton.getCoverElement(),
                    symbol = this.menuButton.getSymbolElement();
                Dyn.assert( options?.menuID && options?.menuToolTip );
                cover.node.setAttribute( "id", options?.menuID );
                cover.node.setAttribute( "aria-label", options?.menuToolTip );
                symbol.data( "tooltip", options?.menuToolTip );
            }
            if( wantMenu !== menuVisible) {
                this.layerVisuals(); // show and adjust title and all buttons to accommodate .menuButton
            }
        }

        /** show or hide the subTitle, expanding the size of the top margin */
        showHideSubtitle( wantVisible ) {
            "use strict";

            if( !wantVisible ) {
                this.resetSubtitle();
            }
            this.bounds.height = this.calcThickness();
        }

        /** show or hide the subtitle, expanding the size of the top margin */
        setSubtitle( newSubTitleString ) {
            "use strict";
            //set the metadata in subtitle
            this.subTitle.attr("text", newSubTitleString);
            this.subTitle.attr({"transform": Dyn.plotUtil.getTextTransform( this.subTitle, "center,top" )});
        }

        // reset the subtitle
        resetSubtitle() {
            "use strict";
            this.subTitle.attr("text", "");
        }

        /**
         * Calculate the desired Top Margin view thickness, so we can
         * determine if our plot view needs to be updated.
         * NOTE: does not actually update the plot view.
         */
        calcThickness() {
            let titleFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotTitleFontSize ),
                subTitleFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotSubtitleFontSize ),
                hasSubtitle = this.subTitle ? (this.subTitle.attr("text").length > 0) : false,
                newThickness = (titleFontSize + (hasSubtitle ? subTitleFontSize : 0 )) / this.kTopAndBottomMarginFraction;
            if( Dyn.plotUtil.isDrawOnly() && ! Dyn.preferences.isPlotTitleVisible()) {
                newThickness = 0; // reclaim title space only if in Draw-Only mode; otherwise we may need the space for icons
            }
            return newThickness;
        }

        setPlotSpecificOptions( options ) {
            // no top margin (plot title) options so far
        }

        /**
         * update our view after changes
         * from Dyn.PlotElement.CalcAxis() or Dyn.PlotShaper.updateAxes()
         */
        postCalcAxisUpdate() {
            "use strict";
            //var plotView = Dyn.plotView;
            // hide the subtitle if we removed an element that previously showed the subtitle.
            //if( ! plotView.plotShaper.hasElement( Dyn.EPlotControl.kControlsMargins )) {
            //	this.showHideSubtitle( false /* hide */ );
            //}
        }

    } // class TopMarginView
    Dyn.TopMarginView = TopMarginView;
    //Dyn.TopMarginView.inheritsFrom( Dyn.MarginView );
    //Dyn.TopMarginView.prototype.kExcludedCasesMaxWhiteSpaceFraction = 0.5; // want excluded cases display to be 50% of whitespace around title or less
    Dyn.TopMarginView.prototype.kTopAndBottomMarginFraction = 0.5; // want top and bottom margin to be 50% of total TopMarginView height

}; // wrapDynTopMarginView
